import { useEffect, useRef } from 'react';
import { BackgroundColor } from 'types/constants';
import NavCoreButton from './NavCoreButton';
import NavCarousel from './NavCarousel';
import NavSubMenu from './NavSubMenu';
import { cn } from 'utils/styles';
import { NavContentLinkProps, NavProps } from './types';
import Button from 'components/shared/Buttons/BaseButton';
import * as m from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import type { TargetAndTransition } from 'motion/react';
import {
  BACKGROUND_FADE_DURATION,
  MENU_CLOSE_DURATION,
  MENU_CONTENT_DURATION,
  MENU_OPEN_DURATION,
  SUB_MENU_DURATION,
} from './animations';
import { HEADER_NAV_ID_PREFIX, navTopBarSelector } from 'components/MainNav/utils';

interface NavContentMenuProps extends Omit<NavProps, 'topBarLink'> {
  subMenuIsOpen: boolean;
  showShadows: boolean;
  activeSubMenu: NavContentLinkProps | null;
  handleMenuScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  handleSubMenuClick: (m: NavContentLinkProps) => void;
  closeMenu: () => void;
}

const NavContentMenu = ({
  subMenuIsOpen,
  showShadows,
  activeSubMenu,
  resourcesLabel,
  resourcesCollection,
  resourcesUrl,
  resourcesInternalPage,
  cta,
  navContent,
  handleMenuScroll,
  handleSubMenuClick,
  closeMenu,
}: NavContentMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const links = navContent.items;

  const slideOutFromLeftAnimation: TargetAndTransition = {
    x: ['-100%', '0%'],
    transition: { ease: 'easeInOut', duration: MENU_OPEN_DURATION, when: 'beforeChildren' },
  };
  const slideInFromRightAnimation: TargetAndTransition = {
    x: ['0%', '-100%'],
    transition: { ease: 'easeInOut', duration: MENU_CLOSE_DURATION },
  };
  const fadeInAndShiftUpAnimation: TargetAndTransition = {
    opacity: [0, 1],
    y: [20, 0],
    transition: { duration: MENU_CONTENT_DURATION, delay: MENU_OPEN_DURATION },
  };
  const fadeInAnimation: TargetAndTransition = {
    opacity: [0, 1],
    transition: { delay: SUB_MENU_DURATION + 0.1, duration: SUB_MENU_DURATION },
  };
  const fadeOutAnimation: TargetAndTransition = {
    opacity: [1, 0],
    transition: { duration: SUB_MENU_DURATION },
  };

  // close on click outside the panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (target instanceof Element && target.closest(navTopBarSelector)) {
        return;
      }
      if (ref.current != null && !ref.current.contains(target)) {
        closeMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [closeMenu]);

  return (
    <m.div
      animate={{ opacity: [0, 1], transition: { when: 'beforeChildren', duration: BACKGROUND_FADE_DURATION } }}
      exit={{
        opacity: [1, 0],
        transition: { when: 'afterChildren', delay: MENU_CLOSE_DURATION, duration: BACKGROUND_FADE_DURATION },
      }}
      className="absolute top-16 sm:top-18 h-[calc(100dvh-var(--menu-offset-height))] sm:h-[calc(100vh-var(--menu-offset-height))] sm:bg-scrim/60 w-full">
      <div className="flex h-full">
        <m.div
          ref={ref}
          animate={slideOutFromLeftAnimation}
          exit={slideInFromRightAnimation}
          className={cn(BackgroundColor.White, 'w-screen sm:w-100 shrink-0 h-full overflow-hidden')}>
          <m.div
            animate={fadeInAndShiftUpAnimation}
            className="p-6 sm:pl-14 max-sm:pb-20 h-full relative overflow-y-auto"
            onScroll={handleMenuScroll}>
            {/* Nav Main Menu */}
            <AnimatePresence>
              {!subMenuIsOpen && (
                <m.div id="nav_main_menu" animate={fadeInAnimation} exit={fadeOutAnimation}>
                  <div className="flex flex-col gap-4 ">
                    {links.map((linkContent, i) => {
                      return <NavCoreButton linkContent={linkContent} handleClick={handleSubMenuClick} key={i} />;
                    })}
                  </div>
                  <div className="border-t border-pebble my-6" />

                  {/* Blog Section */}
                  <NavCarousel
                    resourcesLabel={resourcesLabel}
                    resourcesCollection={resourcesCollection}
                    resourcesUrl={resourcesUrl}
                    resourcesInternalPage={resourcesInternalPage}
                  />
                </m.div>
              )}
            </AnimatePresence>

            {/* SubMenu */}
            <AnimatePresence>
              {subMenuIsOpen && activeSubMenu !== null && (
                <m.div
                  animate={fadeInAnimation}
                  exit={fadeOutAnimation}
                  className="h-full bg-bg-primary absolute top-0">
                  <NavSubMenu {...activeSubMenu} />
                </m.div>
              )}
            </AnimatePresence>
          </m.div>

          {/* Mobile Bottom CTA */}
          <div
            className={cn(
              BackgroundColor.White,
              'sm:hidden flex fixed bottom-0 left-0 w-full p-6 z-50',
              showShadows && 'shadow-[10px_-10px_20px] shadow-kale/5',
            )}>
            <Button
              sys={cta.sys}
              href={cta.url}
              internalPage={cta.internalPage}
              id={`${HEADER_NAV_ID_PREFIX}_mobile_menu_bottom_cta`}>
              {cta.text}
            </Button>
          </div>
        </m.div>
      </div>
    </m.div>
  );
};

export default NavContentMenu;
